import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X, Send, ArrowLeft, Plus, MessageSquare, Clock, AlertTriangle,
  CheckCircle, Loader2, ChevronRight, User, Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_STYLES = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-800',
  high: 'bg-amber-100 text-amber-800',
  urgent: 'bg-red-100 text-red-800',
};
const STATUS_STYLES = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  waiting_on_user: 'bg-purple-100 text-purple-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-600',
};
const STATUS_LABELS = {
  open: 'Open', in_progress: 'In Progress', waiting_on_user: 'Waiting on You',
  resolved: 'Resolved', closed: 'Closed',
};
const CATEGORIES = [
  { value: 'billing', label: 'Billing' },
  { value: 'technical', label: 'Technical' },
  { value: 'account', label: 'Account' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'general', label: 'General' },
];

export default function SupportCenter({ onClose }) {
  const [view, setView] = useState('list');
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [createForm, setCreateForm] = useState({ subject: '', message: '', category: 'general', priority: 'medium' });

  useEffect(() => { loadTickets(); }, []);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/helpdesk/my-tickets`);
      setTickets(res.data.tickets || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!createForm.subject || !createForm.message) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/helpdesk/tickets`, createForm);
      setShowCreate(false);
      setCreateForm({ subject: '', message: '', category: 'general', priority: 'medium' });
      loadTickets();
      openTicket(res.data.ticket_id);
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const openTicket = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/helpdesk/tickets/${ticketId}`);
      setSelectedTicket(res.data);
      setView('detail');
    } catch (e) { console.error(e); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await axios.post(`${API}/helpdesk/tickets/${selectedTicket.ticket_id}/reply`, { message: replyText });
      setReplyText('');
      openTicket(selectedTicket.ticket_id);
    } catch (e) { alert('Failed to send'); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[85vh] flex flex-col" data-testid="support-center">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2DFD8]">
          <div className="flex items-center gap-3">
            {view === 'detail' && (
              <button onClick={() => { setView('list'); setSelectedTicket(null); }} className="p-1 hover:bg-[#F4F2EC] rounded"><ArrowLeft className="w-5 h-5 text-[#475569]" /></button>
            )}
            <div>
              <h2 className="font-heading text-lg font-bold text-[#1B3A6B]">
                {view === 'detail' ? selectedTicket?.subject : 'Support Center'}
              </h2>
              {view === 'detail' && selectedTicket && (
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_STYLES[selectedTicket.status]}`}>{STATUS_LABELS[selectedTicket.status]}</span>
                  <span className="text-[10px] text-[#94A3B8]">{selectedTicket.ticket_id}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {view === 'list' && (
              <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 px-3 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A]" data-testid="create-ticket-btn">
                <Plus className="w-4 h-4" /> New Ticket
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5 text-[#475569]" /></button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {view === 'list' && (
            <div className="p-4">
              {loading ? <div className="text-center py-12 text-[#475569]">Loading...</div> : tickets.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-10 h-10 text-[#E2DFD8] mx-auto mb-3" />
                  <p className="text-[#475569] font-medium">No tickets yet</p>
                  <p className="text-xs text-[#94A3B8] mt-1">Create a ticket to get help from our team</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {tickets.map(t => (
                    <button key={t.ticket_id} onClick={() => openTicket(t.ticket_id)} className="w-full text-left p-4 bg-[#F9F8F6] rounded-xl hover:bg-[#F4F2EC] transition-colors group" data-testid={`ticket-${t.ticket_id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-[#0F1A2A] truncate">{t.subject}</p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_STYLES[t.status]}`}>{STATUS_LABELS[t.status]}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${PRIORITY_STYLES[t.priority]}`}>{t.priority}</span>
                            <span className="text-[10px] text-[#94A3B8]">{t.ticket_id}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-[#94A3B8]">{new Date(t.created_at).toLocaleDateString()}</span>
                          <ChevronRight className="w-4 h-4 text-[#94A3B8] opacity-0 group-hover:opacity-100" />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {view === 'detail' && selectedTicket && (
            <div className="p-4">
              {/* Ticket info */}
              <div className="flex items-center gap-3 mb-4 text-xs text-[#94A3B8]">
                <span className="capitalize">{selectedTicket.category?.replace('_', ' ')}</span>
                <span>·</span>
                <span className={`px-2 py-0.5 rounded-full font-medium ${PRIORITY_STYLES[selectedTicket.priority]}`}>{selectedTicket.priority}</span>
                <span>·</span>
                <span>{new Date(selectedTicket.created_at).toLocaleString()}</span>
              </div>

              {/* Messages */}
              <div className="space-y-3 mb-4">
                {(selectedTicket.messages || []).map(m => (
                  <div key={m.id} className={`flex ${m.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-xl px-4 py-3 ${m.sender_type === 'user' ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC] text-[#0F1A2A]'}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        {m.sender_type === 'admin' ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                        <span className="text-[10px] font-medium opacity-70">{m.sender_name || (m.sender_type === 'admin' ? 'Support' : 'You')}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                      <p className={`text-[10px] mt-1.5 ${m.sender_type === 'user' ? 'text-white/50' : 'text-[#94A3B8]'}`}>{new Date(m.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Status messages */}
              {selectedTicket.status === 'resolved' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-center">
                  <p className="text-sm text-green-800 font-medium flex items-center justify-center gap-1.5"><CheckCircle className="w-4 h-4" /> This ticket has been resolved</p>
                  <p className="text-xs text-green-600 mt-1">Reply to reopen if you still need help</p>
                </div>
              )}
              {selectedTicket.status === 'closed' && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-center">
                  <p className="text-sm text-gray-600">This ticket is closed</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Reply Bar */}
        {view === 'detail' && selectedTicket && selectedTicket.status !== 'closed' && (
          <div className="border-t border-[#E2DFD8] p-4">
            <div className="flex gap-2">
              <input value={replyText} onChange={e => setReplyText(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleReply()} placeholder="Type your reply..." className="flex-1 px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm focus:ring-2 focus:ring-[#1B3A6B]" data-testid="ticket-reply-input" />
              <button onClick={handleReply} disabled={sending || !replyText.trim()} className="px-4 py-2.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-[#254B8A]" data-testid="ticket-reply-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}

        {/* Create Ticket Modal */}
        {showCreate && (
          <div className="absolute inset-0 bg-white rounded-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2DFD8]">
              <h3 className="font-heading text-lg font-bold text-[#1B3A6B]">New Support Ticket</h3>
              <button onClick={() => setShowCreate(false)} className="p-2 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="text-sm font-medium text-[#0F1A2A] block mb-1">Subject *</label>
                <input value={createForm.subject} onChange={e => setCreateForm(p => ({...p, subject: e.target.value}))} placeholder="Brief description of your issue" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="ticket-subject" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-[#0F1A2A] block mb-1">Category</label>
                  <select value={createForm.category} onChange={e => setCreateForm(p => ({...p, category: e.target.value}))} className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="ticket-category">
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F1A2A] block mb-1">Priority</label>
                  <select value={createForm.priority} onChange={e => setCreateForm(p => ({...p, priority: e.target.value}))} className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="ticket-priority">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F1A2A] block mb-1">Message *</label>
                <textarea value={createForm.message} onChange={e => setCreateForm(p => ({...p, message: e.target.value}))} placeholder="Describe your issue in detail..." rows={5} className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="ticket-message" />
              </div>
              <button onClick={handleCreate} disabled={sending || !createForm.subject || !createForm.message} className="w-full py-3 bg-[#1B3A6B] text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#254B8A]" data-testid="ticket-submit">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Submit Ticket
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
